import { Navigate, useParams, Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Eyebrow } from "@/components/site/Motion";
import { CTA } from "@/components/site/CTA";
import { Byline, ArticleCard } from "@/components/insights/ArticleCard";
import { ArticleBody } from "@/components/insights/ArticleBody";
import { ARTICLES, getArticle } from "@/data/insights";

const Progress = () => {
  const { scrollYProgress } = useScroll();
  return <motion.div style={{ scaleX: scrollYProgress }} className="fixed inset-x-0 top-20 z-40 h-0.5 origin-left bg-[#D4AF37]" data-testid="reading-progress" />;
};

const ArticleCTA = ({ lines }) => (
  <section className="relative overflow-hidden bg-[#074E48] text-white grain" data-testid="article-cta">
    <div className="relative mx-auto grid max-w-[1400px] gap-10 px-6 py-20 lg:grid-cols-12 lg:items-end lg:px-12 lg:py-28">
      <Reveal className="space-y-4 lg:col-span-8">
        <Eyebrow light>Continue the conversation</Eyebrow>
        {lines.map((l) => (
          <p key={l} className="font-display text-2xl font-semibold leading-snug md:text-3xl">{l}</p>
        ))}
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-4 lg:text-right">
        <CTA testId="article-cta-button">Talk to KRIMZON</CTA>
      </Reveal>
    </div>
  </section>
);

export default function Article() {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/beyond" replace />;
  const more = ARTICLES.filter((x) => x.slug !== slug);

  return (
    <main data-testid={`article-page-${a.slug}`}>
      <Progress />
      <PageHero key={a.slug} eyebrow={`Beyond · ${a.theme}`} lines={[a.title]} intro={<Byline a={a} />} image={a.image} testId="article-hero" />
      <section className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 lg:grid-cols-12 lg:px-12 lg:py-28">
        <aside className="hidden lg:col-span-3 lg:block">
          <div className="sticky top-32 space-y-4 border-t border-[#D4AF37] pt-6">
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#0D9488]">{a.theme}</p>
            <p className="text-sm text-[#2D6A64]">{a.readTime} min read</p>
            <Link to="/beyond" className="inline-block text-sm font-semibold text-[#0D9488] hover:text-[#074E48]" data-testid="back-to-beyond">← All of Beyond</Link>
          </div>
        </aside>
        <article className="lg:col-span-7 lg:col-start-5">
          <ArticleBody blocks={a.blocks} />
        </article>
      </section>
      <ArticleCTA lines={a.cta} />
      <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-12 lg:py-28">
        <Eyebrow>More from Beyond</Eyebrow>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {more.map((m, i) => (
            <ArticleCard key={m.slug} a={m} i={i} />
          ))}
        </div>
      </section>
    </main>
  );
}
